import { useEffect, useRef, useState } from 'react';

import { Button } from '@apps/web/components/ui/button';
import { Checkbox } from '@apps/web/components/ui/checkbox';
import { MultiSelect } from '@apps/web/components/ui/multi_select';
import { SearchInput } from '@apps/web/components/ui/search_input';
import {
    type CoverageFilters,
    DAY_PARTS,
    type DayPartKey,
    EMPLOYMENT_TYPE_LABELS,
} from '@apps/web/features/coverage/model/coverage_types';
import { type FacetOptions } from '@apps/web/features/coverage/model/filter_model';
import { type CoverageSearchUpdate } from '@apps/web/features/coverage/model/use_coverage_dashboard';
import { useDebouncedValue } from '@apps/web/lib/use_debounced_value';

const SEARCH_DEBOUNCE_MS = 300;

export function CoverageFilterBar({
    filters,
    facetOptions,
    dayLabels,
    filtersActive,
    clearEpoch,
    includeInactive,
    onIncludeInactiveChange,
    onChange,
    onClear,
}: {
    readonly filters: CoverageFilters;
    readonly facetOptions: FacetOptions;
    readonly dayLabels: ReadonlyArray<string>;
    readonly filtersActive: boolean;
    readonly clearEpoch: number;
    readonly includeInactive: boolean;
    readonly onIncludeInactiveChange: (includeInactive: boolean) => void;
    readonly onChange: (update: CoverageSearchUpdate) => void;
    readonly onClear: () => void;
}) {
    const [searchDraft, setSearchDraft] = useState(filters.search);
    const debouncedSearch = useDebouncedValue(searchDraft, SEARCH_DEBOUNCE_MS);
    const searchEditedRef = useRef(false);
    const lastPushedSearchRef = useRef(filters.search);

    useEffect(() => {
        // A URL search value we did not push ourselves (coverage
        // click-through, shared link) is authoritative: adopt it and drop
        // any pending local edit so the debounce can't rewrite it.
        if (filters.search !== lastPushedSearchRef.current) {
            lastPushedSearchRef.current = filters.search;
            searchEditedRef.current = false;
            setSearchDraft(filters.search);
        }
    }, [filters.search]);

    useEffect(() => {
        if (clearEpoch === 0) {
            return;
        }
        // Every clear (filter bar button, empty state, future callers)
        // bumps the epoch; drop the draft even when the URL search value
        // was already empty and the adopt-effect above cannot fire.
        lastPushedSearchRef.current = '';
        searchEditedRef.current = false;
        setSearchDraft('');
    }, [clearEpoch]);

    useEffect(() => {
        if (!searchEditedRef.current) {
            return;
        }
        searchEditedRef.current = false;
        lastPushedSearchRef.current = debouncedSearch;
        onChange({ search: debouncedSearch });
    }, [debouncedSearch, onChange]);

    const toOptions = (values: ReadonlyArray<string>) =>
        values.map((value) => ({ value, label: value }));

    return (
        <div className="flex flex-wrap items-center gap-2">
            <SearchInput
                value={searchDraft}
                onValueChange={(value) => {
                    searchEditedRef.current = true;
                    setSearchDraft(value);
                }}
                placeholder="Search caregivers"
                aria-label="Search caregivers by name"
                containerClassName="w-52"
            />
            <MultiSelect
                options={facetOptions.offices.map(({ key, label }) => ({
                    value: key,
                    label,
                }))}
                selected={[...filters.offices]}
                onChange={(offices) => onChange({ offices })}
                compactLabel="Office"
            />
            <MultiSelect
                options={facetOptions.cities.map(({ key, label }) => ({
                    value: key,
                    label,
                }))}
                selected={[...filters.cities]}
                onChange={(cities) => onChange({ cities })}
                compactLabel="City"
            />
            <MultiSelect
                options={facetOptions.zips.map(({ key, label }) => ({
                    value: key,
                    label,
                }))}
                selected={[...filters.zips]}
                onChange={(zips) => onChange({ zips })}
                compactLabel="ZIP"
            />
            <MultiSelect
                options={dayLabels.map((label, index) => ({
                    value: String(index),
                    label: label || `Day ${index + 1}`,
                }))}
                selected={filters.days.map(String)}
                onChange={(days) =>
                    onChange({ days: days.map(Number).sort((a, b) => a - b) })
                }
                compactLabel="Day"
            />
            <MultiSelect
                options={DAY_PARTS.map((part) => ({
                    value: part.key,
                    label: `${part.label} · ${part.timeLabel}`,
                }))}
                selected={[...filters.dayParts]}
                onChange={(dayParts) =>
                    onChange({ dayParts: dayParts as Array<DayPartKey> })
                }
                compactLabel="Time of day"
            />
            <MultiSelect
                options={toOptions(facetOptions.tags)}
                selected={[...filters.tags]}
                onChange={(tags) => onChange({ tags })}
                compactLabel="Tags"
            />
            <MultiSelect
                options={facetOptions.employmentTypes.map((value) => ({
                    value,
                    label: EMPLOYMENT_TYPE_LABELS[value] ?? value,
                }))}
                selected={[...filters.employmentTypes]}
                onChange={(employmentTypes) => onChange({ employmentTypes })}
                compactLabel="Employment"
            />
            <label className="text-muted-foreground flex cursor-pointer items-center gap-1.5 text-sm">
                <Checkbox
                    checked={includeInactive}
                    onCheckedChange={(checked) =>
                        onIncludeInactiveChange(checked === true)
                    }
                />
                Include inactive
            </label>
            {filters.days.length > 0 && filters.matchMode === 'any' ? (
                <span className="text-muted-foreground bg-muted rounded-full px-2.5 py-0.5 text-xs">
                    Matching any selected day
                </span>
            ) : null}
            {filtersActive ? (
                <Button variant="ghost" size="sm" onClick={onClear}>
                    Clear filters
                </Button>
            ) : null}
        </div>
    );
}
